import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ActionButton from 'common/components/ActionButton';
import AssetType from 'common/enums/assetTypes';
import useFlexBoxStyles from 'common/flexBox';
import { useSettingValue } from 'common/hooks/useSettingValue';
import { getProductDiscount } from 'common/utils/productDiscount';
import { deleteAssetById, setAssets } from 'redux/slices/assetSlice';
import { getDiscounts, setDiscounts } from 'redux/slices/discountSlice';
import { getExpandAccordionEdited, setExpandAccordionEdited } from 'redux/slices/eventSlice';
import { getOrganization } from 'redux/slices/organizationSlice';
import { fetchProductsDiscountsAction, getProductDiscounts } from 'redux/slices/productDiscountSlice';
import EventFormAssetsRender from './EventFormAssetsRender';
import EventFormSelectAllAssetsType from './EventFormSelectAllAssetsType';
import EventFormSelectMultimedia from './EventFormSelectMultimedia';
import EventFormSelectProducts from './EventFormSelectProducts';
import EventDiscountsCreate from './eventFormExtras/eventExtraDiscounts/EventDiscountsCreate';
import EventDiscountsEdit from './eventFormExtras/eventExtraDiscounts/EventDiscountsEdit';

const EventFormAssets = (props) => {
    const { open, assets, close, setStep, cancel, step, enableDiscount } = props;

    const { t } = useTranslation();
    const { classes: flexBox, cx } = useFlexBoxStyles();

    const dispatch = useDispatch();

    const organization = useSelector(getOrganization);
    const viewerExtraFields = useSettingValue(SettingsKey.VIEWER_EXTRA_FIELDS) ?? false;

    const searchMinLength = organization.provider === 'shopify-private' ? 1 : 3;

    const productDiscounts = useSelector(getProductDiscounts);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        setProducts(
            assets
                ?.filter((asset) => asset.type === AssetType.PRODUCT)
                .map((asset) => ({ ...asset, ...getProductDiscount(asset, productDiscounts) })),
        );
    }, [assets, productDiscounts]);

    useEffect(() => {
        dispatch(fetchProductsDiscountsAction({ queryParams: {} }));
    }, [dispatch]);

    const enableUserDataCapture = () => {
        const emailIndex = viewerExtraFields.findIndex((fields) => fields.name === 'email');
        return emailIndex !== -1;
    };

    const expand = useSelector(getExpandAccordionEdited);

    const discounts = useSelector(getDiscounts);

    const deleteDiscount = (idx) => {
        let newArr = [...discounts];
        newArr.splice(idx, 1);
        dispatch(setDiscounts(newArr));
    };
    const addDiscount = () => {
        open(<EventDiscountsCreate closeForm={close} />);
    };

    const editDiscount = (discount, idx) => {
        open(<EventDiscountsEdit discounts={discounts} discount={discount} closeForm={close} idx={idx} />);
    };

    function validateForm() {
        return true; // TODO
    }

    const updateProducts = (products) => {
        dispatch(setExpandAccordionEdited(AssetType.PRODUCT));
        dispatch(
            setAssets([
                ...assets.filter(
                    (asset) =>
                        asset.type !== AssetType.PRODUCT || (asset.type === AssetType.PRODUCT && asset.price === 0),
                ),
                ...products,
            ]),
        );
    };

    const updateAssets = (newAssets) => {
        dispatch(setExpandAccordionEdited('assets'));

        dispatch(
            setAssets([
                ...assets.filter(
                    (asset) =>
                        asset.type !== AssetType.SURVEY &&
                        asset.type !== AssetType.AUCTION &&
                        asset.type !== AssetType.QUIZ &&
                        asset.type !== AssetType.CALL_TO_ACTION &&
                        asset.type !== AssetType.STICKER,
                ),
                ...newAssets,
            ]),
        );
    };

    const updateMultimedia = (multimedia) => {
        dispatch(setExpandAccordionEdited(AssetType.MULTIMEDIA));
        dispatch(setAssets([...assets.filter((asset) => asset.type !== AssetType.MULTIMEDIA), ...multimedia]));
    };

    const selectProducts = () => {
        open(
            <EventFormSelectProducts
                type={AssetType.PRODUCT}
                searchMinLength={searchMinLength}
                close={close}
                selectedProducts={assets.filter((asset) => asset.type === AssetType.PRODUCT && asset.price > 0)}
                onConfirm={updateProducts}
            />,
        );
    };

    const selectAssets = () => {
        const typesSelect = [
            {
                value: AssetType.SURVEY,
                name: t('event.eventFormSelectAssets.survey'),
            },
            {
                value: AssetType.CALL_TO_ACTION,
                name: t('eventFormSelectAssets.callToActions'),
            },
            {
                value: AssetType.STICKER,
                name: t('eventFormSelectAssets.stickers'),
            },
        ];
        open(
            <EventFormSelectAllAssetsType
                enableUserDataCapture={enableUserDataCapture()}
                close={close}
                typesSelect={typesSelect}
                selectedAssets={assets.filter(
                    (asset) =>
                        asset.type === AssetType.SURVEY ||
                        // asset.type === AssetType.AUCTION ||
                        asset.type === AssetType.QUIZ ||
                        asset.type === AssetType.CALL_TO_ACTION ||
                        asset.type === AssetType.STICKER,
                )}
                onConfirm={updateAssets}
            />,
        );
    };

    const selectMultimedia = () => {
        open(
            <EventFormSelectMultimedia
                close={close}
                selectedMultimedia={assets.filter((asset) => asset.type === AssetType.MULTIMEDIA)}
                onConfirm={updateMultimedia}
            />,
        );
    };

    const deleteProduct = (asset) => {
        dispatch(
            deleteAssetById({
                value: asset.variantGroupId || asset.id,
                // idProperty: instantSearchEnabled ? 'variantGroupId' : 'id',
            }),
        );
    };

    const deleteAsset = (asset) => {
        dispatch(deleteAssetById({ value: asset.id }));
    };

    return (
        <Stack direction="column" className={cx(flexBox.overflowHidden, flexBox.h100)}>
            <Stack
                direction="column"
                spacing={1}
                className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.h100)}
            >
                <EventFormAssetsRender
                    expanded={expand === AssetType.MULTIMEDIA}
                    sectionTitle={t('event.eventFormAssets.multimedia')}
                    assets={assets?.filter((asset) => asset.type === AssetType.MULTIMEDIA)}
                    deleteAsset={deleteAsset}
                    selectAsset={selectMultimedia}
                    addButtonText={t('event.eventFormAssets.addMultimedia')}
                />

                <EventFormAssetsRender
                    expanded={expand === 'assets'}
                    sectionTitle={t('eventForm.assets')}
                    assets={assets?.filter(
                        (asset) =>
                            asset.type === AssetType.SURVEY ||
                            asset.type === AssetType.AUCTION ||
                            asset.type === AssetType.QUIZ ||
                            asset.type === AssetType.CALL_TO_ACTION ||
                            asset.type === AssetType.STICKER,
                    )}
                    deleteAsset={deleteAsset}
                    selectAsset={selectAssets}
                    addButtonText={t('eventFormAssets.addAssets')}
                />

                <EventFormAssetsRender
                    expanded={expand === AssetType.PRODUCT}
                    sectionTitle={t('event.eventFormAssets.products')}
                    assets={products}
                    deleteAsset={deleteProduct}
                    selectAsset={selectProducts}
                    addButtonText={t('event.eventFormAssets.addProduct')}
                />

                {enableDiscount && (
                    <EventFormAssetsRender
                        expanded={expand === AssetType.DISCOUNT}
                        sectionTitle={t('event.eventExtraDiscounts.eventExtraDiscounts.discounts')}
                        assets={discounts}
                        deleteAsset={deleteDiscount}
                        selectAsset={addDiscount}
                        editAsset={editDiscount}
                        addButtonText={t('event.eventExtraDiscounts.eventExtraDiscounts.addDiscount')}
                        editButton={true}
                        assetsType={AssetType.DISCOUNT}
                    />
                )}
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                <ActionButton onClick={cancel} color="default" fitWidth>
                    {t('multimedia.multimediaForm.cancel')}
                </ActionButton>

                <ActionButton
                    color="primary"
                    autoFocus
                    fitWidth
                    disabled={!validateForm()}
                    onClick={() => setStep(step + 1)}
                >
                    {t('event.eventFormSettings.save')}
                </ActionButton>
            </Stack>
        </Stack>
    );
};

export default EventFormAssets;
